import GeneratorContext from './GeneratorContext';
import SketchBuilder from './SketchBuilder';

class CompilerEngine {
    /**
     * @param {Object} vm - Scratch VM instance
     * @param {Object} [boardProfile] - Optional board configuration
     */
    static compile(vm, boardProfile = { name: 'Arduino Uno', architecture: 'avr' }) {
        const targets = vm.runtime.targets;
        const target = vm.editingTarget || targets[0];
        if (!target) return '// No targets found\n';

        const blocks = target.blocks._blocks;
        const targetLang = boardProfile.language || 'arduino';
        const context = new GeneratorContext(boardProfile, blocks, targetLang);

        // Pre-allocate global variables from Scratch workspace (Stage + Sprite)
        const stageVariables = targets[0] ? targets[0].variables : {};
        const localVariables = target.variables || {};
        const allVariables = { ...stageVariables, ...localVariables };

        for (const varId in allVariables) {
            const v = allVariables[varId];
            if (v.type === '' || v.type === 'scalar') {
                const varName = context.registerVariable(varId, v.name);
                const val = v.value;
                if (targetLang === 'python') {
                     context.addGlobal(varName, `${varName} = ${typeof val === 'number' ? val : (val ? `"${val}"` : 'None')}`);
                } else {
                     // Since Scratch variables can hold anything, we default to String.
                     // A robust block compiler would infer types, but String avoids
                     // "cannot convert 'String' to 'float'" errors for serial reads.
                     if (typeof val === 'number') {
                         context.addGlobal(varName, `float ${varName} = ${val};`);
                     } else {
                         context.addGlobal(varName, `String ${varName} = "${val}";`);
                     }
                }
            }
        }

        // Find hat blocks (top-level entry points)
        const hatBlocks = Object.values(blocks).filter(b => b.topLevel && !b.shadow);
        
        let loopCodeLines = [];

        for (const hat of hatBlocks) {
            const opcode = hat.opcode;
            let currentId = hat.next;

            // Handle loose scripts
            if (opcode !== 'arduino_setup' && opcode !== 'genericDevice_arduino_setup' && opcode !== 'genericDevice_arduino_loop' && !opcode.startsWith('event_') && opcode !== 'procedures_definition') {
                currentId = hat.id;
            }

            if (opcode === 'procedures_definition') {
                // Handled in block generation natively via Context
                context.getInputValue({ inputs: { dummy: currentId } }, 'dummy');
                continue;
            }

            // Traverse the top level script
            while (currentId) {
                const b = blocks[currentId];
                if (!b) break;

                if (b.opcode === 'control_forever') {
                    // Extract the substack to loop()
                    const substackCode = context.generateSubstack(b, 'SUBSTACK');
                    if (substackCode) loopCodeLines.push(substackCode);
                    break;
                } else {
                    // Evaluate standard sequential blocks
                    const code = context.getInputValue({ inputs: { dummy: currentId } }, 'dummy');
                    if (code) {
                        if (opcode === 'genericDevice_arduino_loop') {
                            // If attached to the explicit Arduino Loop hat, route to loop() instead of setup()
                            loopCodeLines.push(code + (code.endsWith(';') || code.endsWith('}') ? '' : ';'));
                        } else {
                            // By default, loose scripts or explicit setup scripts map to setup()
                            context.addSetup(`${code};`);
                        }
                    }
                }
                currentId = b.next;
            }
        }

        return SketchBuilder.build(context, loopCodeLines.join('\n'));
    }
}

export default CompilerEngine;
